import { useStyletron } from "baseui";
import { Block } from "baseui/block";
import { Button } from "baseui/button";
import { HeadingMedium, LabelSmall, ParagraphMedium, ParagraphSmall, ParagraphXSmall } from "baseui/typography";
import { useState } from "react";
import { VscSymbolClass, VscTrash } from "react-icons/vsc";
import { LoadingView } from "../../components/LoadingView";
import {
  useGetResourceGroupAHUOptimizersQuery,
  useGetResourceGroupBacnetDevicesQuery,
  useGetResourceGroupByIdQuery,
} from "../../services/resourceGroups/resourceGroupApi";
import { DeleteResourceGroupDialog } from "./";
import { HorizontalStackPanel, VerticalStackPanel } from "../../components/Layout/StackPanel";
import { IoHardwareChipOutline } from "react-icons/io5";
import { ListItem, ListItemLabel } from "baseui/list";

type ResourceGroupProps = {
  resourceGroupId: string;
  subscriptionId: string;
};
const ResourceGroup = ({ resourceGroupId, subscriptionId }: ResourceGroupProps) => {
  const [css, theme] = useStyletron();
  const { data: rg, isLoading, isSuccess, isError, error } = useGetResourceGroupByIdQuery(resourceGroupId);
  const [isOpen, setIsOpen] = useState(false);
  const { data: bacnetDevices } = useGetResourceGroupBacnetDevicesQuery(rg?.id ?? "", { skip: !rg });
  const { data: ahus } = useGetResourceGroupAHUOptimizersQuery(rg?.id ?? "", {
    skip: !rg,
  });

  return (
    <Block width="100%" className={css({ position: "relative" })}>
      <LabelSmall color={theme.colors.accent}>Resource Group</LabelSmall>
      <LoadingView isLoading={isLoading} isSucess={isSuccess} error={error}>
        <HeadingMedium margin={0}>
          <VscSymbolClass color={theme.colors.primary} /> {rg?.name}
        </HeadingMedium>
        <ParagraphXSmall>{resourceGroupId}</ParagraphXSmall>
        {rg && (
          <>
            <Button
              shape="circle"
              kind="secondary"
              className={css({
                position: "absolute",
                top: "-" + theme.sizing.scale800,
                right: theme.sizing.scale500,
              })}
              disabled={!rg}
              onClick={() => {
                setIsOpen(true);
              }}>
              <VscTrash />
            </Button>
            <DeleteResourceGroupDialog
              subscriptionId={subscriptionId}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              resourceGroup={rg}
            />
          </>
        )}
        <br />
        <VerticalStackPanel>
          <HorizontalStackPanel $flexWrap={"nowrap"}>
            <ParagraphMedium margin={0}>
              <IoHardwareChipOutline /> <b>{bacnetDevices?.length}</b> {"BACnet Device(s)"}
            </ParagraphMedium>
          </HorizontalStackPanel>
          {bacnetDevices?.map((device) => (
            <ListItem
              shape="ROUND"
              overrides={{
                Root: {
                  style: ({ $theme }) => ({
                    backgroundColor: device.disabled ? $theme.colors.backgroundSecondary : undefined,
                    opacity: device.disabled ? 0.5 : undefined,
                    filter: device.disabled ? "grayscale(100%)" : undefined,
                  }),
                },
              }}>
              <ListItemLabel description={<>BACnet objects: {device.usedObjects}</>}>
                [{device.deviceId}] {device.description}{" "}
              </ListItemLabel>
            </ListItem>
          ))}
          {ahus && ahus?.length > 0 && (
            <HorizontalStackPanel $flexWrap={"nowrap"}>
              <ParagraphSmall margin={0}>
                {" "}
                <b>{ahus?.length}</b> {"AHU Optimizer(s)"}
              </ParagraphSmall>
            </HorizontalStackPanel>
          )}
        </VerticalStackPanel>
      </LoadingView>
    </Block>
  );
};

export default ResourceGroup;
